import { HttpMethod } from "../../enums/HttpMethod";
import { PaymentFrequency } from "../../../common/interfaces/enums/PaymentFrequency";
import { ApiCaseConfig } from "../../interfaces/api/group/results/IApiExistingApplication";
import { IApiQuoteGroupCard } from "../../interfaces/api/group/results/IApiQuoteGroupCard";
import { IApiCreateQuote } from "../../interfaces/api/IApiCreateQuote";
import { IClient } from "../../../common/interfaces/IClient";
import { IMapper } from "../../interfaces/IMapper";
import IQuoteCardResult from "../../interfaces/results/IQuoteCardResult";
import { ApiGroupCase } from "../../types/api/ApiGroupCase";
import { ApiResponseMessage, ApiResponseNoWrap } from "../../types/api/ApiResponses";
import { QuoteSettingContainerType } from "../../../common/interfaces/settings/QuoteSettingContainerType";
import { apiFetch } from "../helpers";
import EnumMapper from "../mapper/EnumMapper";
import { mapServerQuoteCardToLocal, retrieveGroupMapper } from "../mapper/mapper";

export const clearAllConfigsAPI = (groupId: string): Promise<boolean> => {
    return apiFetch<ApiResponseNoWrap<void>>(`/api/v1/groups/${groupId}/cases/clear`, {
        method: HttpMethod.Patch
    })
        .then(() => true)
        .catch(() => false);
};

export const getSharedQuoteAPI = async (
    caseId: number,
    freq: PaymentFrequency
): Promise<{
    results: IQuoteCardResult[];
    config: QuoteSettingContainerType;
}> => {
    const config = await apiFetch<ApiResponseNoWrap<ApiCaseConfig>>(
        `/api/v1/quote/config/${caseId}`,
        { method: HttpMethod.Get }
    ).then((res) => {
        const productType = EnumMapper.Product.remoteToLocal(res.productType);

        const mapper = retrieveGroupMapper(productType);

        return mapper.remoteToLocal(res);
    });

    // As these end points are not for the main application, we don't expose them for the app to use.
    const results = await apiFetch<ApiResponseNoWrap<IApiQuoteGroupCard[]>>(
        `/api/quote-engine?caseId=${caseId}&frequency=${EnumMapper.PaymentFrequency.localToRemote(
            freq
        )}`,
        { method: HttpMethod.Get }
    ).then((res) => mapServerQuoteCardToLocal(res));

    return {
        results,
        config
    };
};

export const createQuoteAPI = <T extends QuoteSettingContainerType>(
    mapper: IMapper<T>,
    clients: IClient[],
    settings: T
): Promise<number> => {
    const config = mapper.localToRemote(clients, settings);

    return apiFetch<ApiResponseNoWrap<IApiCreateQuote>>(
        `/api/v1/${settings.productType.toString()}/quote/config`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(config)
        }
    )
        .then((res) => res.caseId || 0)
        .catch(e => 0);
};

export const getMultiQuoteResultAPI = (
    groupId: string,
    frequency: PaymentFrequency
): Promise<IQuoteCardResult[]> => {
    return apiFetch<ApiResponseNoWrap<IApiQuoteGroupCard[]>>(
        `/api/v1/groups/${groupId}/results/all?frequency=${frequency}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => mapServerQuoteCardToLocal(res));
};

export const getQuoteResultsAPI = (
    groupId: string,
    caseId: number
): Promise<IQuoteCardResult[]> => {
    return apiFetch<ApiResponseNoWrap<IApiQuoteGroupCard[]>>(
        `/api/v1/groups/${groupId}/results/cases/${caseId}`,
        {
            method: HttpMethod.Get
        }
    ).then(async (res) => {
        return mapServerQuoteCardToLocal(res);
    });
};

export const addQuoteToGroupAPI = (
    groupId: string,
    caseId: number,
    isActive = true
): Promise<ApiGroupCase> => {
    return apiFetch<ApiResponseNoWrap<ApiGroupCase>>(
        `/api/v1/groups/${groupId}/cases/${caseId}?isActive=${isActive}`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    ).then((res) => res);
};

export const selectQuoteCaseAPI = (
    groupId: string,
    caseId: number,
    isSelected = true
): Promise<ApiGroupCase> => {
    return apiFetch<ApiResponseNoWrap<ApiGroupCase>>(
        `/api/v1/groups/${groupId}/cases/${caseId}/select?isSelected=${isSelected}`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    );
};

export const selectProviderAddOnAPI = (
    groupId: string,
    addOnSysName: string,
    isSelected: boolean
): Promise<void> => {
    return apiFetch(`/api/quote/${groupId}/addons/${addOnSysName}?isSelected=${isSelected}`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        }
    });
};
